<template>
  <Record class="RecordPageBatch">
    <Route slot="title" :id="pageBatch.id" class="title" to="PageBatch">
      {{ $t('name', [pageBatch.id]) }}
    </Route>
    <div class="pageSteps">
      <div
        v-for="({ count, countFail, countSuccess, icon, label }) in batchSteps"
        :key="label">
        <Set :icon="icon" :label="label">
          {{ count }}
          <Tag v-if="countFail" class="danger inv">
            <Icon name="exclamation" /> {{ countFail }}
          </Tag>
          <Tag v-if="countSuccess" class="success inv">
            <Icon name="check" /> {{ countSuccess }}
          </Tag>
        </Set>
      </div>
    </div>
    <div slot="meta">
      {{ $d(pageBatch.createdAt, 'short') }}
    </div>
    <div slot="actions">
      <Btn
        class="ib"
        :class="pageBatch.isPaused ? 'warning' : 'info'"
        :disabled="isDeleting"
        :icon="pageBatch.isPaused ? 'play' : 'pause'"
        :isLoading="isPausing"
        @click="togglePause">
        {{ pageBatch.isPaused ? $t('resume') : $t('pause') }}
      </Btn>
      <Btn
        class="ib warning"
        :disabled="isDeleting"
        icon="refresh"
        :isLoading="isInitiatingRerun"
        @click="rerunBatch">
        {{ $t('rerun') }}
      </Btn>
      <Btn
        class="ib warning"
        :disabled="isDeleting"
        icon="refresh"
        :isLoading="isInitiatingRetry"
        @click="retryFailed">
        {{ $t('retryFailed') }}
      </Btn>
      <Btn
        class="ib danger"
        :disabled="!pageBatch.isPaused"
        icon="trash-alt"
        :isLoading="isDeleting"
        @click="deleteBatch">
        {{ $t('delete') }}
      </Btn>
    </div>
  </Record>
</template>

<script>
import Record from '@components/Record'
import Set from '@components/Set'
import { actions } from '@utils'
import { prop } from 'rambda'

export default {
  components: { Record, Set },
  props: {
    pageBatch: Object,
  },
  data() {
    return {
      isDeleting: false,
      isInitiatingRerun: false,
      isInitiatingRetry: false,
      isPausing: false,
    }
  },
  computed: {
    batchSteps() {
      const initialSteps = [
        {
          label: this.$t('urlsInitial'),
          count: this.pageBatch.urlsInitial.length,
          countFailed: this.pageBatch.urlsInitial.filter(prop('failedAt')).length,
          countSuccess: this.pageBatch.urlsInitial.filter(prop('doneAt')).length,
        },
      ]

      const pageSteps = [
        ['crawl', 'search'],
        ['parse', 'laptop'],
        ['fill', 'newspaper'],
        ['media', 'images'],
      ].map(([step, icon]) => ({
        icon,
        count: this.pageBatch[`${step}Success`],
        countFail: this.pageBatch[`${step}Fail`],
        label: this.$t(`${step}Success`),
      }))

      return [
        ...initialSteps,
        {
          label: this.$t('unprocessed'),
          count: this.pageBatch.pending,
        },
        ...pageSteps,
      ]
    },
  },
  methods: {
    deleteBatch() {
      this.PROMPT_DELETE({
        name: this.$t('name', [this.pageBatch.id]),
        confirm: () => this.$wrap(
          this.DELETE_PAGE_BATCH(this.pageBatch),
          'isDeleting',
        ),
      })
    },
    rerunBatch() {
      this.$wrap(this.PATCH_PAGE_BATCH({
        id: this.pageBatch.id,
        $rerunPageBatch: 'ALL',
      }), 'isInitiatingRerun')
    },
    retryFailed() {
      this.$wrap(this.PATCH_PAGE_BATCH({
        id: this.pageBatch.id,
        $rerunPageBatch: 'FAILED',
      }), 'isInitiatingRetry')
    },
    togglePause() {
      this.$wrap(this.PATCH_PAGE_BATCH({
        id: this.pageBatch.id,
        isPaused: !this.pageBatch.isPaused,
      }), 'isPausing')
    },
    ...actions(
      'DELETE_PAGE_BATCH',
      'PATCH_PAGE_BATCH',
      'PROMPT_DELETE',
    ),
  },
}
</script>

<style lang="scss">
.RecordPageBatch {
  .title {
    border: 0;
    display: inline-block;
  }

  .Tag {
    margin-right: 0.25rem;

    .Loader {
      color: $white;
      display: inline-block;
      vertical-align: middle;
    }
  }

  .pageSteps {
    display: flex;
    justify-content: space-between;
  }
}
</style>

<i18n>
{
  "en": {
    "crawlSuccess": "Crawled",
    "createdAt": "Date created",
    "delete": "Delete",
    "fillSuccess": "Fill",
    "mediaSuccess": "Illustrated",
    "name": "Batch {0}",
    "parseSuccess": "Parsed",
    "pause": "Pause",
    "rerun": "Re-run batch",
    "retryFailed": "Retry failed",
    "resume": "Resume",
    "unprocessed": "Unprocessed",
    "urlsInitial": "Seed"
  }
}
</i18n>
